const LOG_STORAGE_KEY = "sunridgeBugReportLogs";
const ACTIVITY_STORAGE_KEY = "sunridgeBugReportActivity";
const MAX_LOGS = 120;
const MAX_ACTIVITY = 120;
const REDACTED = "[redacted]";
const SENSITIVE_KEY_PATTERN = /token|authorization|password|secret|cookie|session/i;

const isBrowser = () => typeof window !== "undefined";

const readStoredArray = (key) => {
  if (!isBrowser()) return [];

  try {
    const raw = window.sessionStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeStoredArray = (key, value) => {
  if (!isBrowser()) return;

  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Session diagnostics should never block the portal.
  }
};

const notifyDiagnosticsUpdated = () => {
  if (!isBrowser()) return;
  window.dispatchEvent(new CustomEvent("sunridge:bug-diagnostics-updated"));
};

export const redactDiagnostics = (value, depth = 0) => {
  if (depth > 5) return "[max-depth]";
  if (value === null || value === undefined) return value;

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }

  if (Array.isArray(value)) {
    return value.slice(0, 50).map((item) => redactDiagnostics(item, depth + 1));
  }

  if (typeof value === "object") {
    return Object.entries(value).reduce((safe, [key, item]) => {
      safe[key] = SENSITIVE_KEY_PATTERN.test(key)
        ? REDACTED
        : redactDiagnostics(item, depth + 1);
      return safe;
    }, {});
  }

  if (typeof value === "string" && value.length > 2000) {
    return value.slice(0, 2000);
  }

  return value;
};

const serializeLogArg = (arg) => {
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  if (typeof arg === "string") return arg;

  try {
    return JSON.stringify(redactDiagnostics(arg));
  } catch {
    return String(arg);
  }
};

const buildBaseEntry = () => ({
  timestamp: new Date().toISOString(),
  url: isBrowser() ? window.location.href : "",
  route: isBrowser() ? `${window.location.pathname}${window.location.search}` : "",
});

export const pushBugReportLog = (entry) => {
  const logs = readStoredArray(LOG_STORAGE_KEY);
  const safeEntry = {
    ...buildBaseEntry(),
    level: entry?.level || "log",
    source: entry?.source || "client",
    message: String(entry?.message || "").slice(0, 2000),
    stack: entry?.stack ? String(entry.stack).slice(0, 3000) : undefined,
    metadata: redactDiagnostics(entry?.metadata || {}),
  };

  writeStoredArray(LOG_STORAGE_KEY, [...logs, safeEntry].slice(-MAX_LOGS));
  notifyDiagnosticsUpdated();
};

export const pushBugReportActivity = (entry) => {
  const activity = readStoredArray(ACTIVITY_STORAGE_KEY);
  const safeEntry = {
    ...buildBaseEntry(),
    type: String(entry?.type || "action").slice(0, 80),
    label: String(entry?.label || "").slice(0, 500),
    path: isBrowser() ? `${window.location.pathname}${window.location.search}` : "",
    metadata: redactDiagnostics(entry?.metadata || {}),
  };

  writeStoredArray(ACTIVITY_STORAGE_KEY, [...activity, safeEntry].slice(-MAX_ACTIVITY));
  notifyDiagnosticsUpdated();
};

export const getBugReportLogs = () => readStoredArray(LOG_STORAGE_KEY);

export const getBugReportActivity = () => readStoredArray(ACTIVITY_STORAGE_KEY);

export const clearBugReportDiagnostics = () => {
  writeStoredArray(LOG_STORAGE_KEY, []);
  writeStoredArray(ACTIVITY_STORAGE_KEY, []);
  notifyDiagnosticsUpdated();
};

export const recordBugReportRoute = (path) => {
  pushBugReportLog({
    level: "navigation",
    source: "router",
    message: `Navigated to ${path}`,
  });
  pushBugReportActivity({
    type: "navigation",
    label: `Navigated to ${path}`,
  });
};

const getElementLabel = (element) => {
  if (!element) return "";
  return (
    element.getAttribute("aria-label") ||
    element.getAttribute("title") ||
    element.getAttribute("name") ||
    element.id ||
    element.textContent ||
    element.tagName ||
    ""
  )
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 180);
};

const installConsoleCapture = () => {
  const consoleMethods = ["log", "info", "warn", "error", "debug"];
  window.__sunridgeBugReportConsoleOriginals = window.__sunridgeBugReportConsoleOriginals || {};

  consoleMethods.forEach((level) => {
    if (window.__sunridgeBugReportConsoleOriginals[level]) return;

    const original = window.console?.[level]?.bind(window.console);
    if (!original) return;

    window.__sunridgeBugReportConsoleOriginals[level] = original;
    window.console[level] = (...args) => {
      original(...args);
      pushBugReportLog({
        level,
        source: "console",
        message: args.map(serializeLogArg).join(" "),
        stack: args.find((arg) => arg instanceof Error)?.stack,
      });
    };
  });
};

const installErrorCapture = () => {
  window.addEventListener("error", (event) => {
    pushBugReportLog({
      level: "error",
      source: "window.error",
      message: event.message,
      stack: event.error?.stack,
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    pushBugReportLog({
      level: "error",
      source: "unhandledrejection",
      message: reason?.message || serializeLogArg(reason),
      stack: reason?.stack,
    });
  });
};

const installNetworkCapture = () => {
  if (window.fetch && !window.__sunridgeBugReportOriginalFetch) {
    window.__sunridgeBugReportOriginalFetch = window.fetch.bind(window);

    window.fetch = async (...args) => {
      const startedAt = Date.now();
      const request = args[0];
      const url = typeof request === "string" ? request : request?.url;
      const method = args[1]?.method || request?.method || "GET";

      try {
        const response = await window.__sunridgeBugReportOriginalFetch(...args);
        if (!response.ok) {
          pushBugReportLog({
            level: "network",
            source: "fetch",
            message: `${method} ${url} returned ${response.status}`,
            metadata: {
              method,
              url,
              status: response.status,
              durationMs: Date.now() - startedAt,
            },
          });
        }
        return response;
      } catch (error) {
        pushBugReportLog({
          level: "network",
          source: "fetch",
          message: `${method} ${url} failed: ${error.message}`,
          stack: error.stack,
          metadata: {
            method,
            url,
            durationMs: Date.now() - startedAt,
          },
        });
        throw error;
      }
    };
  }

  if (window.XMLHttpRequest && !window.__sunridgeBugReportOriginalXHROpen) {
    const originalOpen = window.XMLHttpRequest.prototype.open;
    const originalSend = window.XMLHttpRequest.prototype.send;

    window.__sunridgeBugReportOriginalXHROpen = originalOpen;
    window.__sunridgeBugReportOriginalXHRSend = originalSend;

    window.XMLHttpRequest.prototype.open = function bugReportOpen(method, url, ...rest) {
      this.__sunridgeBugReportRequest = {
        method,
        url,
        startedAt: Date.now(),
      };
      return originalOpen.call(this, method, url, ...rest);
    };

    window.XMLHttpRequest.prototype.send = function bugReportSend(...args) {
      this.addEventListener("loadend", () => {
        const request = this.__sunridgeBugReportRequest;
        if (!request || this.status < 400) return;

        pushBugReportLog({
          level: "network",
          source: "xhr",
          message: `${request.method} ${request.url} returned ${this.status}`,
          metadata: {
            method: request.method,
            url: request.url,
            status: this.status,
            durationMs: Date.now() - request.startedAt,
          },
        });
      });

      return originalSend.call(this, ...args);
    };
  }
};

const installActivityCapture = () => {
  document.addEventListener(
    "click",
    (event) => {
      const target = event.target?.closest?.("button,a,[role='button'],input,textarea,select");
      if (!target || target.closest("[data-bug-reporter='true']")) return;

      pushBugReportActivity({
        type: "click",
        label: getElementLabel(target),
        metadata: {
          tagName: target.tagName,
          type: target.getAttribute("type"),
          href: target.getAttribute("href"),
        },
      });
    },
    true
  );
};

export const installBugReportDiagnostics = () => {
  if (!isBrowser()) return false;
  if (window.__sunridgeBugReportDiagnosticsInstalled) return true;

  window.__sunridgeBugReportDiagnosticsInstalled = true;
  installConsoleCapture();
  installErrorCapture();
  installNetworkCapture();
  installActivityCapture();

  pushBugReportLog({
    level: "info",
    source: "bug-reporter",
    message: "Bug report diagnostics initialized",
  });

  return true;
};

export const getCurrentUserDiagnostics = () => {
  if (!isBrowser()) return {};

  const safeParse = (key) => {
    try {
      const raw = window.localStorage.getItem(key);
      return raw ? redactDiagnostics(JSON.parse(raw)) : undefined;
    } catch {
      return undefined;
    }
  };

  return redactDiagnostics({
    user: window.localStorage.getItem("user"),
    userShowName: window.localStorage.getItem("userShowName"),
    cognitoUser: safeParse("cognitoUser"),
  });
};
